import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { BookOpen, RotateCcw, Sparkles } from 'lucide-react'
import { learningApi } from '@/api/learning'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import type { ContinueLearning } from '@/api/dashboard'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import { copy } from '@/lib/copy'

interface ContinueLearningCardProps {
  continueLearning: ContinueLearning
  isNewUser: boolean
}

function CardShell({
  children,
  action,
}: {
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <Card
      className="border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-5"
      data-testid="continue-learning-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">{children}</div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </Card>
  )
}

export function ContinueLearningCard({ continueLearning, isNewUser }: ContinueLearningCardProps) {
  const { actions, pages, dashboard } = useMinCopy()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)

  const navigateFromPlan = (plan: {
    resumeSessionId: string | null
    ctaLabel: string
    lastCompletedSessionId: string | null
  }) => {
    if (plan.resumeSessionId) {
      navigate(`/learn/${plan.resumeSessionId}`)
      return true
    }

    if (plan.ctaLabel === 'KEEP') {
      if (plan.lastCompletedSessionId) {
        navigate(`/learn/${plan.lastCompletedSessionId}`)
        return true
      }
      navigate('/reading')
      return true
    }

    return false
  }

  const startMutation = useMutation({
    mutationFn: () => learningApi.startSession(),
    onSuccess: (session) => {
      navigate(`/learn/${session.id}`)
    },
    onError: async (error) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        try {
          const plan = await learningApi.getPlan()
          if (navigateFromPlan(plan)) {
            return
          }
        } catch {
          // Fall through to generic error.
        }
      }
      setError(dashboard.startFailed)
    },
    onSettled: () => {
      setIsStarting(false)
    },
  })

  const isPending = isStarting || startMutation.isPending

  if (isNewUser) {
    const canStart = continueLearning.canStart

    return (
      <CardShell
        action={
          canStart ? (
            <Button
              className="w-full sm:w-auto sm:min-w-[180px]"
              data-testid="continue-learning-cta"
              loading={isPending}
              onClick={() => {
                setError(null)
                startMutation.mutate()
              }}
            >
              {actions.startReading}
            </Button>
          ) : (
            <Link to="/reading/recommended">
              <Button className="w-full sm:w-auto sm:min-w-[180px]" data-testid="continue-learning-cta">
                {actions.startReading}
              </Button>
            </Link>
          )
        }
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-primary">{dashboard.continueLearningEyebrow}</p>
            <h2 className="mt-0.5 text-lg font-semibold tracking-tight">
              {copy.onboarding.heroTitle}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground" data-testid="onboarding-prompt">
              {copy.onboarding.dashboardPrompt}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/80">{copy.onboarding.dashboardHint}</p>
            {canStart ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {formatMessage(dashboard.guidedSessionMinutes, {
                  minutes: continueLearning.estimatedMinutes,
                })}
              </p>
            ) : null}
            {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
          </div>
        </div>
      </CardShell>
    )
  }

  if (!continueLearning.canStart) {
    return (
      <CardShell
        action={
          <Link to="/reading">
            <Button className="w-full sm:w-auto sm:min-w-[180px]" data-testid="continue-learning-cta">
              {actions.browseReading}
            </Button>
          </Link>
        }
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-medium text-primary">{dashboard.continueLearningEyebrow}</p>
            <p className="mt-1 text-sm text-muted-foreground">{actions.browseReading}</p>
          </div>
        </div>
      </CardShell>
    )
  }

  const {
    estimatedMinutes,
    dueCount,
    resumeSessionId,
    ctaLabel,
    guidedSessionCompletedToday,
  } = continueLearning

  const isKeepMode = ctaLabel === 'KEEP'
  const ctaText = isKeepMode ? pages.keepLearning : pages.continueLearning
  const sessionLabel = isKeepMode ? dashboard.sessionLabelComplete : dashboard.sessionLabelToday

  const headline = isKeepMode
    ? dashboard.headlineReadyMore
    : resumeSessionId
      ? dashboard.headlineResume
      : dueCount > 0
        ? dueCount === 1
          ? dashboard.headlineWordsReadyOne
          : formatMessage(dashboard.headlineWordsReady, { count: dueCount })
        : formatMessage(dashboard.headlineMinutesSession, { minutes: estimatedMinutes })

  const detail = isKeepMode
    ? guidedSessionCompletedToday
      ? dashboard.detailDailyDone
      : dashboard.detailWhatNext
    : resumeSessionId
      ? dashboard.detailWaiting
      : dueCount > 0
        ? formatMessage(dashboard.detailAboutMinutes, { minutes: estimatedMinutes })
        : dashboard.detailCaughtUp

  const handleStart = async () => {
    setError(null)
    setIsStarting(true)

    try {
      const plan = await learningApi.getPlan()
      if (navigateFromPlan(plan)) {
        setIsStarting(false)
        return
      }

      startMutation.mutate()
    } catch {
      setIsStarting(false)
      setError(dashboard.startFailed)
    }
  }

  const Icon = resumeSessionId ? RotateCcw : dueCount > 0 ? RotateCcw : Sparkles
  const reviewDue = dueCount > 0 && !resumeSessionId && !isKeepMode
  const reviewCtaLabel =
    dueCount === 1 ? actions.readyToRefreshOne : formatMessage(actions.readyToRefresh, { count: dueCount })

  return (
    <CardShell
      action={
        reviewDue ? (
          <Link to="/review">
            <Button className="w-full sm:w-auto sm:min-w-[180px]" data-testid="continue-learning-cta">
              {reviewCtaLabel}
            </Button>
          </Link>
        ) : (
          <Button
            className="w-full sm:w-auto sm:min-w-[180px]"
            data-testid="continue-learning-cta"
            loading={isPending}
            onClick={handleStart}
          >
            {ctaText}
          </Button>
        )
      }
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-primary">{sessionLabel}</p>
          <h2 className="mt-0.5 text-lg font-semibold tracking-tight">{headline}</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{detail}</p>
          {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
        </div>
      </div>
    </CardShell>
  )
}
